import { Dialog, DialogBody, DialogFooter, DialogHeader } from "@material-tailwind/react";
import { IconButton } from "@mui/material";
import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Stateful } from "../../hooks/useStateful";
import BottomLine from "../BottomLine";
import ButtonDefault, { ButtonLayout } from "../Button/ButtonDefault";
import { CloseIcon } from "../Icons/Icons";

export enum DialogAlignment {
  Start,
  Center,
}

export type DialogDefaultProps = {
  title: string;
  message: string | ReactElement;
  open: Stateful<boolean>;
  aligment?: DialogAlignment;
  buttons?: ReactElement;
  buttonLayout?: ButtonLayout;
  isBottomModal?: boolean;
  closeModal?: boolean;
} & React.ComponentProps<"div">;

export default function DialogDefault({
  title,
  message,
  open,
  aligment,
  buttons,
  buttonLayout,
  isBottomModal = false,
  closeModal = true,
  ...restProps
}: DialogDefaultProps): JSX.Element {
  const { t } = useTranslation();
  const defaultCloseButton = (
    <ButtonDefault
      id="close"
      label={t("actions.close")}
      icon={<CloseIcon />}
      onClick={() => open.set(false)}
    />
  );
  const dialogClassName =
    aligment === undefined || aligment === DialogAlignment.Start
      ? "flex flex-col"
      : "flex flex-col justify-center items-center";

  const buttonsClassName = `flex mt-6 gap-2 ${
    buttonLayout === ButtonLayout.Vertical
      ? "flex-col items-center"
      : `justify-end items-start flex-nowrap ${
          buttonLayout === ButtonLayout.HorizontalReverse ? "flex-row-reverse" : "flex-row "
        }`
  }`;

  if (!isBottomModal) {
    return (
      <Dialog
        id={restProps.id}
        className={dialogClassName + " rounded-3xl overflow-y-auto max-h-[90vh] "}
        open={open.value}
        handler={open.set}
        dismiss={{ outsidePress: closeModal }}
      >
        <DialogHeader className="text-ldir-brown-900 p-6 pb-0"> {title}</DialogHeader>
        <DialogBody className="text-ldir-brown-900 text-lg p-6 pt-5 ">
          {message || t("")}
        </DialogBody>
        <DialogFooter className={buttonsClassName}>{buttons || defaultCloseButton}</DialogFooter>
      </Dialog>
    );
  }
  return (
    <Dialog
      dismiss={{ outsidePress: closeModal }}
      id={restProps.id}
      className={
        "bottom-0! left-1/2! translate-x-[-50%]! fixed min-w-fit w-full max-w-175 min-h-[66vh] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-t-2xl rounded-b-none shadow-lg flex flex-col m-0"
      }
      open={open.value}
      handler={open.set}
    >
      <div className="text-right mx-5 mt-4">
        {closeModal ? (
          <IconButton onClick={() => open.set(false)}>
            <CloseIcon className="text-ldir-brown-900" />
          </IconButton>
        ) : (
          <div className="h-6" />
        )}
        <BottomLine />
      </div>
      <DialogHeader className="text-ldir-brown-900 mt-15 mx-10 p-0 text-2xl font-bold">
        {title}
      </DialogHeader>
      <DialogBody className="text-ldir-brown-900 text-2xl mb-12 mt-5 p-0 mx-10 font-normal">
        {message}
      </DialogBody>
      <DialogFooter className={"flex flex-col grow gap-5 pt-0 pb-6"}>
        {buttons || defaultCloseButton}
      </DialogFooter>
    </Dialog>
  );
}
